/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Bell, ChevronLeft, MapPin, ShoppingCart as ShoppingCartIcon, Truck } from "lucide-react";
import { useNavigate } from "react-router-dom";

import FarmlandsLogo from "../../../../assets/images/farmlands-pro-logo-colored-column.svg";
import { useIsFirstLevelRoute } from "../../../../components/layout/hooks/useIsFirstLevelRoute";
import { TestIds } from "../../../../testIds";
import onClickDeveloper from "../../../../util/exportAuth";
import { HOME_ROUTE } from "../../../../util/routing";
import { MOBILE_HEADER_PILL_BUTTON_CLASSES } from "../../constants/mobile-header-button-styles";
import { useCartHeaderData } from "../../hooks/useCartHeaderData";

import { MobileIconButton } from "./MobileIconButton";

export const MobileCartHeader = () => {
  const navigate = useNavigate();
  const isFirstLevelRoute = useIsFirstLevelRoute();
  const {
    canUse,
    canCreateCartOrder,
    canViewProShop,
    cartItemCount,
    notificationCount,
    showNotifications,
    isDelivery,
    pickupOrDeliveryAddress,
    goToCart,
    openNotifications,
    openPickupModal,
  } = useCartHeaderData();

  if (!canUse) {
    return <div className="min-h-16.5 w-full" />;
  }

  const PickupIcon = isDelivery ? Truck : MapPin;

  return (
    <div
      className={`flex min-h-16.5 w-full items-center justify-between px-4 text-forest-green-950`}
    >
      <div className="flex w-11 items-center justify-center">
        {isFirstLevelRoute ? (
          <img
            src={FarmlandsLogo}
            alt="Farmlands logo"
            className="max-w-11 cursor-pointer"
            onClick={() => {
              navigate(HOME_ROUTE);
              if (process.env.NODE_ENV === "development") {
                onClickDeveloper();
              }
            }}
          />
        ) : (
          <MobileIconButton
            icon={ChevronLeft}
            ariaLabel="Back"
            testId={TestIds.components.cartHeader.backButton}
            onClick={() => navigate(-1)}
          />
        )}
      </div>
      {canViewProShop && (
        <button
          type="button"
          onClick={openPickupModal}
          data-testid={TestIds.components.cartHeader.pickupDeliveryShortcutButton}
          className={MOBILE_HEADER_PILL_BUTTON_CLASSES}
        >
          <PickupIcon aria-hidden size={24} />
          <span
            className={`
              max-w-[calc(80vw-11.25rem)] truncate text-left text-sm underline
              [text-decoration-skip-ink:none]
              [text-underline-position:from-font]
            `}
          >
            {pickupOrDeliveryAddress ?? "Pickup / delivery"}
          </span>
        </button>
      )}
      <div className="flex items-center gap-(--gap-xs)">
        {showNotifications && (
          <MobileIconButton
            icon={Bell}
            count={notificationCount}
            ariaLabel="Notifications"
            testId={TestIds.components.cartHeader.inboxButton}
            onClick={openNotifications}
          />
        )}
        {canCreateCartOrder && (
          <MobileIconButton
            icon={ShoppingCartIcon}
            count={cartItemCount}
            ariaLabel="Cart"
            testId={TestIds.components.cartHeader.cartShortcutButton}
            onClick={goToCart}
          />
        )}
      </div>
    </div>
  );
};

export default MobileCartHeader;
